import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { LinkElem } from 'modules/Header/styles';
import PropTypes from 'prop-types';
import { URLS, PERMISSIONS, USER_ROLE_TYPES } from '_constants';
import { useDispatch, useSelector } from 'react-redux';
import { signOut } from '_entities/Auth/Auth.actions';
import { removeToken } from 'utils/services/token';
import { getUserRoleType } from '_entities/Auth/Auth.selectors';
import LogOutPopup from '../../../LogOutPopup';
import * as Styled from './styles';

function MenuItem({
  route,
  setIsMenuOpen,
  setIsBurgerMenu,
  parentRef,
  index,
  focusedTab,
  setFocusedTab,
  routeRef,
  handleSetParentFocus,
  burgerRef,
}) {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const userRoleType = useSelector(getUserRoleType);

  const handleLogInAsUser = async () => {
    try {
      await dispatch(signOut());
    } catch (e) {
      // Non-fatal: token is still cleared and login flow continues in finally.
    } finally {
      removeToken();
      history.push(URLS.login, { as_user: true });
    }
  };

  const handleSelect = useCallback(
    (e, path) => {
      if (path === location.pathname) {
        e.preventDefault();
        return;
      }
      e.stopPropagation();
      setIsMenuOpen(false);
      setIsBurgerMenu(false);
      setFocusedTab(index);
      burgerRef?.current?.focus();
    },
    [burgerRef, index, location.pathname, setFocusedTab, setIsBurgerMenu, setIsMenuOpen],
  );

  const handleSelectByKeyboard = useCallback(
    (e, path, isHelp) => {
      if ((path === location.pathname && e.keyCode !== 27) || e.keyCode === 9) {
        setIsMenuOpen(false);
        document.getElementById('burgerMenuMobileId').focus();
        e.preventDefault();
        return;
      }
      if ([27].includes(e.keyCode)) {
        setIsMenuOpen(false);
        parentRef?.current?.focus();
        handleSetParentFocus();
      }
      if ([32, 13].includes(e.keyCode) && !isHelp) {
        e.preventDefault();
        setIsBurgerMenu(false);
        history.push(path);
        burgerRef?.current?.focus();
      }
    },
    [burgerRef, handleSetParentFocus, history, location.pathname, parentRef, setIsBurgerMenu, setIsMenuOpen],
  );

  const handleLogOut = useCallback(() => {
    setIsOpenPopup(true);
  }, [setIsOpenPopup]);

  return (
    <>
      <Styled.ListItem border={route.border} borderAll={route.borderAll} key={route.id} role="menuitem">
        {route.path !== URLS.logout && (
          <LinkElem
            onClick={(e) => handleSelect(e, route.path)}
            onKeyDown={(e) => handleSelectByKeyboard(e, route.path, route?.isHelp)}
            to={route.isHelp ? { pathname: route.href } : route.path}
            name={route.path}
            aria-label={route.title}
            ref={routeRef}
            target={route.isHelp && '_blank'}
            tabIndex={focusedTab === index ? 0 : -1}
          >
            {route.title}
          </LinkElem>
        )}
        {route.path === URLS.logout && route.permission !== PERMISSIONS.LOGIN_AS_USER && (
          <>
            <Styled.LogOut
              onClick={handleLogOut}
              aria-label={route.title}
              tabIndex={focusedTab === index ? 0 : -1}
              ref={routeRef}
            >
              {route.title}
            </Styled.LogOut>
            <LogOutPopup isOpenPopup={isOpenPopup} setIsOpenPopup={setIsOpenPopup} />
          </>
        )}
        {route.path === URLS.logout &&
          route.permission === PERMISSIONS.LOGIN_AS_USER &&
          userRoleType === USER_ROLE_TYPES.MULTI_ACCOUNT_MANAGER && (
            <Styled.LogOut
              onClick={() => handleLogInAsUser()}
              onKeyDown={(e) => {
                if ([13].includes(e.keyCode)) {
                  handleLogInAsUser();
                }
              }}
              aria-label={route.title}
              ref={routeRef}
              tabIndex={focusedTab === index ? 0 : -1}
            >
              {route.title}
            </Styled.LogOut>
          )}
      </Styled.ListItem>
    </>
  );
}

MenuItem.propTypes = {
  route: PropTypes.shape({
    path: PropTypes.string,
    href: PropTypes.string,
    title: PropTypes.string,
    id: PropTypes.number,
    devider: PropTypes.string,
    border: PropTypes.bool,
    isHelp: PropTypes.bool,
    isDevider: PropTypes.bool,
    borderAll: PropTypes.bool,
    permission: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }).isRequired,
  setIsMenuOpen: PropTypes.func.isRequired,
  setIsBurgerMenu: PropTypes.func.isRequired,
  parentRef: PropTypes.object.isRequired,
  handleSetParentFocus: PropTypes.func.isRequired,
  burgerRef: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  focusedTab: PropTypes.number.isRequired,
  setFocusedTab: PropTypes.func.isRequired,
  routeRef: PropTypes.object.isRequired,
};

export default MenuItem;
